import { Trans, useLingui } from '@lingui/react/macro';
import { Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';

import type {
  CloudConnectSubscription,
  Plan,
} from 'src/api/endpoints/cloudConnect.types';
import {
  Button,
  Divider,
  FlexDivider,
  Modal,
  Sheet,
  TextWithLabel,
} from 'src/components';
import { formatAmount } from 'src/utils/productUtils';

import { useOfferExpirationDetails } from '../utils/getOfferExpirationDetails';
import { useHasClickedBiennialOffer } from '../utils/hasBiennialOfferStore';
import { CardHeading } from './CardHeading';
import { CloudConnectChangePaymentIntervalToAnnualForm } from './CloudConnectChangePaymentIntervalToAnnualForm';
import { CloudConnectChangePaymentIntervalToBiennialForm } from './CloudConnectChangePaymentIntervalToBiennialForm';
import { CloudConnectChangePaymentIntervalToMonthlyForm } from './CloudConnectChangePaymentIntervalToMonthlyForm';

const routeApi = getRouteApi('/_auth/_layout/subscriptions/payment');

const getPlanPrice = (plan: Plan) => {
  switch (plan.interval) {
    case 'monthly':
      return plan.prices.monthly;
    case 'annual':
      return plan.prices.annual;
    case 'biennial':
      return plan.prices.biennial ?? null;
  }
};

const getPriceForInterval = (
  subscription: CloudConnectSubscription,
  interval: CloudConnectSubscription['planCurrent']['interval']
) => {
  const plan =
    subscription.planCurrent.interval === interval
      ? subscription.planCurrent
      : subscription.planOptions.find((plan) => plan.interval === interval);

  if (!plan) {
    return null;
  }

  switch (interval) {
    case 'monthly':
      return plan.prices.monthly;
    case 'annual':
      return plan.prices.annual;
    case 'biennial':
      return plan.prices.biennial ?? null;
  }
};

export const CloudConnectPaymentIntervalCard = ({
  subscription,
}: {
  subscription: CloudConnectSubscription;
}) => {
  const { t, i18n } = useLingui();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const [hasClickedBiennialOffer, setHasClickedBiennialOffer] =
    useHasClickedBiennialOffer();
  const displayedPlan = subscription.planUpcoming ?? subscription.planCurrent;
  const price = getPlanPrice(displayedPlan);
  const monthlyPrice = getPriceForInterval(subscription, 'monthly');
  const annualPrice = getPriceForInterval(subscription, 'annual');
  const biennialPrice = getPriceForInterval(subscription, 'biennial');
  const biennialOfferHasEnded = useOfferExpirationDetails().offerHasEnded;
  const isActuallyBiennial = subscription.planCurrent.interval === 'biennial';
  // Customers affected by a since-fixed bug may have a Stripe schedule that will (incorrectly) move them to
  // biennial at the end of their current period, instead of applying immediately as intended. For these customers,
  // `planUpcoming` reports biennial even though they're not actually being billed for it yet. Re-enable the switch
  // so they can self-service a "reset": submitting again goes through the (now fixed) immediate-switch flow, which
  // releases the stale schedule and applies biennial correctly.
  const hasPendingScheduleToBiennial =
    !isActuallyBiennial && subscription.planUpcoming?.interval === 'biennial';
  const hasBiennialOffer = useMemo(
    () =>
      !isActuallyBiennial &&
      !!biennialPrice &&
      (hasPendingScheduleToBiennial ||
        (hasClickedBiennialOffer && !biennialOfferHasEnded)),
    [
      biennialOfferHasEnded,
      biennialPrice,
      hasClickedBiennialOffer,
      hasPendingScheduleToBiennial,
      isActuallyBiennial,
    ]
  );
  const canSwitchToAnnual =
    displayedPlan.interval === 'monthly' && !!annualPrice;
  const canSwitchToMonthly =
    displayedPlan.interval !== 'monthly' && !!monthlyPrice;
  const nextInterval = canSwitchToMonthly ? 'monthly' : 'annual';
  const annualPriceText = annualPrice
    ? formatAmount(annualPrice.totalAmount, annualPrice.currency, i18n.locale)
    : '';
  const monthlyPriceText = monthlyPrice
    ? formatAmount(monthlyPrice.totalAmount, monthlyPrice.currency, i18n.locale)
    : '';
  const perMonthLabel = <Trans context="Pricing period label">/month</Trans>;

  useEffect(() => {
    if (search.updateBillingInterval === 'biennial') {
      setHasClickedBiennialOffer(true);
    }

    if (search.updateBillingInterval === 'true') {
      void navigate({
        to: '.',
        search: (previous) => ({
          ...previous,
          updateBillingInterval: nextInterval,
        }),
      });
    }
  }, [
    navigate,
    nextInterval,
    search.updateBillingInterval,
    setHasClickedBiennialOffer,
  ]);

  return (
    <Sheet className="gap-24">
      <CardHeading>
        <Trans>Payment interval</Trans>
      </CardHeading>

      <Divider />

      <div className="flex w-full flex-col flex-wrap justify-between gap-16 lm:flex-row">
        <TextWithLabel label={t`Current interval`} className="">
          {displayedPlan.interval === 'monthly' && t`Monthly`}
          {displayedPlan.interval === 'annual' && t`Annually`}
          {displayedPlan.interval === 'biennial' && t`2-year`}
        </TextWithLabel>

        {price && (
          <>
            <FlexDivider />
            {displayedPlan.interval === 'monthly' && (
              <TextWithLabel
                label={t`Price per month`}
                className="flex-1 shrink-0 whitespace-nowrap"
              >
                {formatAmount(price.totalAmount, price.currency, i18n.locale)}{' '}
                <span className="font-light">
                  (
                  {formatAmount(
                    price.totalAmount * 12,
                    price.currency,
                    i18n.locale
                  )}
                  <Trans context="Pricing period label">/year</Trans>)
                </span>
              </TextWithLabel>
            )}

            {displayedPlan.interval === 'annual' && (
              <TextWithLabel label={t`Price per year`} className="">
                {formatAmount(price.totalAmount, price.currency, i18n.locale)}{' '}
                <span className="font-light">
                  (
                  {formatAmount(
                    price.totalAmount / 12,
                    price.currency,
                    i18n.locale
                  )}
                  {perMonthLabel})
                </span>
              </TextWithLabel>
            )}

            {displayedPlan.interval === 'biennial' && (
              <TextWithLabel label={t`Price per 2 years`} className="flex-1">
                {formatAmount(price.totalAmount, price.currency, i18n.locale)}{' '}
                <span className="font-light">
                  (
                  {formatAmount(
                    price.totalAmount / 24,
                    price.currency,
                    i18n.locale
                  )}
                  {perMonthLabel})
                </span>
              </TextWithLabel>
            )}
          </>
        )}

        {(canSwitchToAnnual || canSwitchToMonthly || biennialPrice) && (
          <div className="flex flex-col items-start gap-8 lm:flex-row">
            <Modal.Root
              open={
                search.updateBillingInterval === 'annual' ||
                search.updateBillingInterval === 'monthly' ||
                search.updateBillingInterval === 'biennial'
              }
              onOpenChange={(open) => {
                void navigate({
                  to: '.',
                  search: (previous) => ({
                    ...previous,
                    updateBillingInterval: open ? nextInterval : undefined,
                  }),
                });
              }}
            >
              {(canSwitchToAnnual || canSwitchToMonthly) && (
                <Modal.Trigger asChild>
                  <Button
                    variant={canSwitchToAnnual ? 'primary' : 'tertiary'}
                    size="medium"
                    className="w-full ls:w-fit"
                    data-cy="change-payment-interval-button"
                  >
                    {canSwitchToAnnual ? (
                      <Trans>Switch to annual billing</Trans>
                    ) : (
                      <Trans>Switch to monthly billing</Trans>
                    )}
                  </Button>
                </Modal.Trigger>
              )}

              <Modal.Content>
                {search.updateBillingInterval === 'annual' && annualPrice && (
                  <CloudConnectChangePaymentIntervalToAnnualForm
                    annualPrice={annualPrice}
                    currentPlan={displayedPlan}
                    subscription={subscription}
                  />
                )}
                {search.updateBillingInterval === 'monthly' && monthlyPrice && (
                  <CloudConnectChangePaymentIntervalToMonthlyForm
                    currentPlan={displayedPlan}
                    monthlyPrice={monthlyPrice}
                    subscription={subscription}
                  />
                )}
                {search.updateBillingInterval === 'biennial' && (
                  <CloudConnectChangePaymentIntervalToBiennialForm
                    biennialPrice={biennialPrice}
                    currentPlan={subscription.planCurrent}
                    hasPendingScheduleToBiennial={hasPendingScheduleToBiennial}
                    subscription={subscription}
                  />
                )}
              </Modal.Content>
            </Modal.Root>
            {hasBiennialOffer && (
              <Button
                onClick={() => {
                  void navigate({
                    to: '.',
                    search: (previous) => ({
                      ...previous,
                      updateBillingInterval: 'biennial',
                    }),
                  });
                }}
                size="medium"
                className="w-full ls:w-fit"
                variant="primary"
              >
                <Trans>Switch to 2-year billing</Trans>
              </Button>
            )}
          </div>
        )}
      </div>

      {displayedPlan.interval === 'monthly' && annualPrice && (
        <Typography
          as="p"
          variant="body-sm"
          className="text-center font-regular italic xs:text-left"
        >
          <Trans>
            Pay{' '}
            <b className="font-bold" data-cy="annual-price">
              {annualPriceText}/year
            </b>{' '}
            <span className="whitespace-nowrap">and get two months free.</span>
          </Trans>
        </Typography>
      )}

      {displayedPlan.interval === 'annual' && monthlyPrice && (
        <Typography
          as="p"
          variant="body-sm"
          className="text-center font-regular italic xs:text-left"
        >
          <Trans>
            <span>Want to pay monthly?</span>
            &nbsp; Pay{' '}
            <b className="font-bold" data-cy="monthly-price">
              {monthlyPriceText}/month
            </b>
          </Trans>
        </Typography>
      )}
    </Sheet>
  );
};
